import { useState } from "react";
import { Bug, Share2, Upload, X } from "lucide-react";
import {
  shareLogFile,
  uploadLogsToServer,
  type LogFileExport,
} from "../lib/logger";
import { useAuth } from "../contexts/auth-context";
import { handleApiAuthError } from "../lib/auth-required";

type Props = {
  exported: LogFileExport;
  apiBase: string;
  versionCode?: number;
  versionName?: string;
  onClose: () => void;
};

/**
 * After generating a PDF problem report: share the real .txt via system sheet
 * and/or upload to the server — never leave the user with only a Cache path.
 */
export function PdfReportSheet({
  exported,
  apiBase,
  versionCode,
  versionName,
  onClose,
}: Props) {
  const { requireAuth } = useAuth();
  const [sharing, setSharing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const share = async () => {
    if (sharing) return;
    setSharing(true);
    setStatus(null);
    try {
      await shareLogFile(exported, {
        title: "گزارش مشکل PDF",
        dialogText: `گزارش مشکل PDF — ${exported.fileName}`,
      });
      setStatus("صفحهٔ اشتراک سیستم باز شد — می‌توانید فایل را در واتساپ و… بفرستید.");
    } catch (err) {
      setStatus(
        err instanceof Error
          ? `اشتراک ناموفق: ${err.message}`
          : "اشتراک فایل ناموفق بود."
      );
    } finally {
      setSharing(false);
    }
  };

  const upload = async () => {
    if (uploading) return;
    requireAuth(async () => {
      setUploading(true);
      setStatus(null);
      try {
        const result = await uploadLogsToServer(apiBase, {
          kind: "pdf-report",
          text: exported.text,
          fileName: exported.fileName,
          versionCode,
          versionName,
        });
        if (!result.ok) {
          setStatus(result.error || "آپلود ناموفق");
        } else {
          setStatus(
            `گزارش به سرور ارسال شد${result.id ? ` (${result.id})` : ""}.`
          );
        }
      } catch (err) {
        if (handleApiAuthError(err, () => void upload())) return;
        setStatus(err instanceof Error ? err.message : "آپلود ناموفق");
      } finally {
        setUploading(false);
      }
    });
  };

  return (
    <div
      className="pdf-report-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="pdf-report-sheet"
        role="dialog"
        aria-label="گزارش مشکل PDF"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pdf-report-header">
          <span className="pdf-report-title">
            <Bug size={16} />
            گزارش مشکل PDF
          </span>
          <button
            type="button"
            className="icon-btn"
            aria-label="بستن"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
        <p className="settings-hint" dir="rtl">
          فایل آماده است:{" "}
          <span dir="ltr">{exported.fileName}</span>
          <br />
          آن را با اپ‌های دیگر بفرستید یا مستقیم به سرور آپلود کنید.
        </p>
        <div className="pdf-report-actions">
          <button
            type="button"
            className="icon-btn primary"
            disabled={sharing}
            onClick={() => void share()}
          >
            <Share2 size={15} />
            {sharing ? "در حال اشتراک…" : "اشتراک فایل"}
          </button>
          <button
            type="button"
            className="icon-btn"
            disabled={uploading}
            onClick={() => void upload()}
          >
            <Upload size={15} />
            {uploading ? "در حال ارسال…" : "آپلود به سرور"}
          </button>
        </div>
        {status && (
          <p className="settings-hint" dir="rtl" role="status">
            {status}
          </p>
        )}
      </div>
    </div>
  );
}
